import { Button, Stack } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IconButton, InformationModal } from 'utoniq-ui'

import {
  ArtistThreadCategory,
  Maybe,
  ThreadEditQuery,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { FullscreenModal } from '@/components/organisms/functionals'
import { ThreadCreatePreview } from '@/components/pages/ThreadCreate/template/ThreadCreatePreview'
import {
  handleError,
  useForm,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import {
  defaultValues,
  schema,
  ThreadEditForm,
  ThreadFormValues,
} from './ThreadEditForm'

export * from './fragments'

export type State = {
  thread: Maybe<ThreadEditQuery['artistThreadChannel']>
  artist: Maybe<ThreadEditQuery['artist']>
  user: Maybe<ThreadEditQuery['user']>
}

type ActionLinkCellProps = {
  disabled: boolean
  onConfirmSaveDraft: () => void
  onShowPreview: () => void
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onEditThread: (inputs: ThreadFormValues) => Promise<void>
  onSaveDraft: (inputs: ThreadFormValues) => Promise<void>
}

export const ThreadEditTemplate: Template<State, Action> = ({
  state,
  onEditThread,
}) => {
  const { t } = useTranslation('threadCreate')
  const { router, routerNames, generatePath, routerPages } =
    useRouter<{ threadId: string }>()
  const defaultBackPage = generatePath(routerPages.ArtistThreadDetail, {
    threadId: router.query.threadId ?? '#',
  })
  const { showMessage, showError } = useMessage()
  const [open, setOpen] = useState<boolean>(false)
  const [artistDraftSaving, setArtistDraftSaving] = useState<boolean>(false)
  const [artistEditing, setArtistEditing] = useState<boolean>(false)
  const [openPreview, setOpenPreview] = useState<boolean>(false)
  const {
    handleSubmit,
    register,
    control,
    setValue,
    getValues,
    reset,
    setError,
    formState: { errors, isValid, isDirty },
  } = useForm<ThreadFormValues>({
    schema,
    defaultValues,
  })
  const categoryOptions = useMemo(
    () =>
      categories.map(category => ({
        value: category,
        label: t([`category_${category}`, category]),
      })),
    [t]
  )

  useEffect(() => {
    if (!state.thread) {
      return
    }
    reset({
      category: (state.thread.category as ArtistThreadCategory) ?? undefined,
      description: state.thread.description,
      isPremium: state.thread.isPremium,
      name: state.thread.name,
      thumbnail: {
        fileUrl: state.thread.thumbnail?.mediumUri ?? '',
      },
    })
  }, [state.thread, reset])

  const onSubmit = handleSubmit(async values => {
    if (!isDirty) {
      return router.goBack(defaultBackPage)
    }
    setArtistEditing(true)
    try {
      await onEditThread(values)
      router.goBack(defaultBackPage)
      showMessage(t('updateComplete'), 'success')
    } catch (error) {
      handleError<ThreadFormValues>(error as Error, setError, showError)
    }
    setArtistEditing(false)
  })

  const handleSaveDraft = handleSubmit(async values => {
    setArtistDraftSaving(true)
    try {
      /**
       * @description
       * 下書き保存機能は別タスクにて実装
       * https://app.clickup.com/t/908127/UT-5151
       *
       * await onSaveDraft(values)
       * setOpen(false)
       */
      router.goBack(defaultBackPage)
    } catch (error) {
      showError(error as Error)
    }
    setArtistDraftSaving(false)
  })

  const handleShowPreview = useCallback(() => {
    setOpenPreview(true)
  }, [])

  const handleCloseWithoutSave = useCallback(() => {
    setOpen(false)
    router.goBack(defaultBackPage)
  }, [router, defaultBackPage])

  return (
    <>
      <Head title={routerNames.ArtistThreadEdit} />

      <BaseLayout
        navigation={{
          type: 'threadForm',
          props: {
            disabled: !isValid,
            onConfirmSaveDraft: handleSaveDraft,
            onShowPreview: handleShowPreview,
          },
        }}
        header={{
          left: (
            <IconButton.Close
              edge="start"
              onClick={() => {
                /**
                 * @description
                 * 下書き保存機能は別タスクにて実装
                 * https://app.clickup.com/t/908127/UT-5151
                 *
                 * if (isDirty) {
                 *  setOpen(true)
                 *  return
                 * }
                 */
                return router.goBack(defaultBackPage)
              }}
            />
          ),
          title: t('title'),
          right: (
            <Button
              type="submit"
              size="small"
              disabled={!isValid}
              loading={artistEditing}
              onClick={onSubmit}
              sx={{ mr: -1 }}
            >
              {t('public')}
            </Button>
          ),
        }}
      >
        <ThreadEditForm
          hasPatron={!!state?.artist?.patron?.id}
          control={control}
          errors={errors}
          onSubmit={onSubmit}
          register={register}
          thread={getValues()}
          setValue={setValue}
          userId={state.user?.id ?? ''}
          categoryOptions={categoryOptions}
        />
      </BaseLayout>

      <InformationModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('draftTitle')}
        content={t('draftDesc')}
        buttons={
          <>
            <Button
              sx={{ minWidth: 212 }}
              variant="contained"
              color="primary"
              size="large"
              loading={artistDraftSaving}
              onClick={handleSaveDraft}
            >
              {t('saveToDraft')}
            </Button>
            <Button
              sx={{ minWidth: 212 }}
              variant="outlined"
              color="primary"
              size="large"
              disabled={artistDraftSaving}
              onClick={handleCloseWithoutSave}
            >
              {t('closeWithoutSave')}
            </Button>
          </>
        }
      />

      <FullscreenModal open={openPreview} onClose={() => setOpenPreview(false)}>
        <ThreadCreatePreview
          artistId={state.thread?.artist?.id}
          onClose={() => setOpenPreview(false)}
          thread={getValues()}
          categoryOptions={categoryOptions}
          user={state.user ?? null}
        />
      </FullscreenModal>
    </>
  )
}

const ActionLinkCell: Cell<ActionLinkCellProps> = ({
  onConfirmSaveDraft,
  onShowPreview,
  disabled,
}) => {
  const { t } = useTranslation('threadCreate')
  const actions = [
    {
      title: t('saveDraft'),
      onClick: onConfirmSaveDraft,
    },
    {
      title: t('preview'),
      onClick: onShowPreview,
    },
  ]
  return (
    <CellFrame sx={{ bgcolor: 'background.default' }}>
      <Stack direction="row" spacing={3} sx={{ p: 3 }}>
        {actions.map((action, index) => {
          /**
           * @description
           * 下書き保存機能は別タスクにて実装
           * https://app.clickup.com/t/908127/UT-5151
           */
          if (action.title === t('saveDraft')) return

          return (
            <Button
              key={index}
              variant="text"
              onClick={action.onClick}
              disabled={disabled}
              disableElevation
              sx={{
                bgcolor: 'transparent',
                color: 'text.primary',
                '&.Mui-disabled': {
                  bgcolor: 'transparent',
                  color: 'text.disabled',
                },
              }}
            >
              {action.title}
            </Button>
          )
        })}
      </Stack>
    </CellFrame>
  )
}

const categories = [
  ArtistThreadCategory.TradeCall,
  ArtistThreadCategory.Event,
  ArtistThreadCategory.Request,
  ArtistThreadCategory.Faq,
]
